/**
 * prefetchStream — abre conexión WHEP headless (sin <video>) para dejar la
 * cámara warm en el streamPool. Cuando el operador luego hace click, el
 * mount del WhepPlayer encuentra hit en el pool y attach es <100ms.
 *
 * Sprint Performance Live — Día 4 (prefetch hover).
 *
 * Estrategia:
 *   - Se llama desde onMouseEnter de markers de mapa o items de árbol de
 *     cámaras. Con debounce 200ms para evitar storm si el mouse pasa rápido.
 *   - Si la key ya está en pool → no hace nada.
 *   - Si está in-flight (otro hover lanzó) → reusa la Promise.
 *   - Si falla negotiate → silent, no rompe nada.
 *
 * Importa solo donde se usa para no pesar el bundle inicial.
 */

import { streamPool } from './streamPool'

const _inflight = new Map()   // key → Promise

function _log(...args) {
  if (typeof window !== 'undefined' && window.__STREAM_POOL_DEBUG) {
    console.log('[prefetchStream]', ...args)
  }
}

export async function prefetchStream({ siteId, channel, token }) {
  if (siteId == null || channel == null || !token) return
  const key = `whep-${siteId}-${channel}`

  // Ya en pool — no hacemos nada (acquire + release neutral)
  const existing = streamPool.acquire(key)
  if (existing) {
    streamPool.release(key)
    _log('skip — ya en pool', key)
    return
  }

  // Ya in-flight (otro hover anterior la pidió, esperar)
  if (_inflight.has(key)) {
    _log('skip — in-flight', key)
    return _inflight.get(key)
  }

  const p = (async () => {
    let pc = null
    try {
      _log('start', key)
      const gr = await fetch(
        `/api/streams/${siteId}/grant?channel=${channel}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      )
      if (!gr.ok) { _log('grant fail', gr.status); return }
      const grant = await gr.json()

      pc = new RTCPeerConnection({
        iceServers: grant.ice_servers || [],
        bundlePolicy: 'max-bundle',
      })
      pc.addTransceiver('video', { direction: 'recvonly' })
      pc.addTransceiver('audio', { direction: 'recvonly' })

      let stream = null
      const trackP = new Promise((resolve) => {
        pc.ontrack = (e) => {
          if (e.streams[0]) { stream = e.streams[0]; resolve() }
        }
      })

      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)

      // Esperar gathering complete (MediaMTX WHEP requiere SDP con todos los
      // candidates al primer POST; trickle ICE no soportado).
      await new Promise((resolve) => {
        if (pc.iceGatheringState === 'complete') return resolve()
        const on = () => {
          if (pc.iceGatheringState === 'complete') {
            pc.removeEventListener('icegatheringstatechange', on)
            resolve()
          }
        }
        pc.addEventListener('icegatheringstatechange', on)
        setTimeout(() => {
          pc.removeEventListener('icegatheringstatechange', on)
          resolve()
        }, 3000)
      })

      const ar = await fetch(grant.whep_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/sdp',
          'Authorization': `Bearer ${grant.stream_token}`,
        },
        body: pc.localDescription.sdp,
      })
      if (!ar.ok) { _log('answer fail', ar.status); try { pc.close() } catch {}; return }
      const answer = await ar.text()
      await pc.setRemoteDescription({ type: 'answer', sdp: answer })

      // Esperar ICE connected + primer track (timeout 8s).
      await Promise.race([
        new Promise((resolve) => {
          pc.oniceconnectionstatechange = () => {
            const s = pc.iceConnectionState
            if (s === 'connected' || s === 'completed') resolve()
          }
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('ice timeout')), 8000)),
      ])
      await Promise.race([
        trackP,
        new Promise((_, reject) => setTimeout(() => reject(new Error('track timeout')), 5000)),
      ])

      if (stream && pc.iceConnectionState !== 'failed') {
        const capturedPc = pc
        streamPool.put(key, {
          pc: capturedPc, stream,
          close: () => { try { capturedPc.close() } catch {} },
        })
        // El prefetch no consume refCount — release para que LRU pueda
        // expulsarla si llega otra cámara más reciente.
        streamPool.release(key)
        _log('PREFETCHED', key)
      } else {
        try { pc.close() } catch {}
      }
    } catch (e) {
      _log('error', key, e?.message || e)
      if (pc) { try { pc.close() } catch {} }
    } finally {
      _inflight.delete(key)
    }
  })()

  _inflight.set(key, p)
  return p
}
